import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Search, UserPlus, X, Users } from 'lucide-react'
import { toast } from 'sonner'
import * as areasApi from '@/modules/users/api/areasApi'
import { apiClient } from '@/shared/services/client'
import { areasKeys } from '@/modules/users/hooks/useAreas'
import type { AreaUsuario } from '@/modules/users/types/area'

interface SimpleUser {
  id: number
  email: string
  first_name: string
  last_name: string
}

interface AreaUsersDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  areaId: number | null
  areaName: string
}

export function AreaUsersDialog({
  open,
  onOpenChange,
  areaId,
  areaName,
}: AreaUsersDialogProps) {
  const queryClient = useQueryClient()
  const [userSearch, setUserSearch] = useState('')
  const [searchResults, setSearchResults] = useState<SimpleUser[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isAssigning, setIsAssigning] = useState(false)

  const { data: areaUsers, isLoading } = useQuery({
    queryKey: ['area-users', areaId],
    queryFn: () => areasApi.getAreaUsers(areaId!),
    enabled: open && !!areaId,
  })

  useEffect(() => {
    if (!open) {
      setUserSearch('')
      setSearchResults([])
    }
  }, [open])

  // Search users with debounce
  useEffect(() => {
    if (userSearch.length < 2) {
      setSearchResults([])
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const { data } = await apiClient.get('/users/', {
          params: { search: userSearch, page_size: 10 },
        })
        const results = (data.results || []) as SimpleUser[]
        // Filter out already assigned users
        const assignedIds = new Set(areaUsers?.map((u: AreaUsuario) => u.id) || [])
        setSearchResults(results.filter((u: SimpleUser) => !assignedIds.has(u.id)))
      } catch {
        setSearchResults([])
      }
      setIsSearching(false)
    }, 300)

    return () => clearTimeout(timer)
  }, [userSearch, areaUsers])

  const handleAssign = async (userId: number) => {
    if (!areaId) return
    setIsAssigning(true)
    try {
      await areasApi.assignUserToArea(areaId, userId)
      queryClient.invalidateQueries({ queryKey: ['area-users', areaId] })
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      setSearchResults((prev) => prev.filter((u) => u.id !== userId))
      toast.success('Usuario asignado')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al asignar usuario')
    }
    setIsAssigning(false)
  }

  const handleRemove = async (userId: number) => {
    if (!areaId) return
    try {
      await areasApi.removeUserFromArea(areaId, userId)
      queryClient.invalidateQueries({ queryKey: ['area-users', areaId] })
      queryClient.invalidateQueries({ queryKey: areasKeys.lists() })
      queryClient.invalidateQueries({ queryKey: areasKeys.allList() })
      toast.success('Usuario removido')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al remover usuario')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-teal-500" />
            Usuarios de {areaName}
          </DialogTitle>
          <DialogDescription>
            Gestiona los usuarios asignados a esta área
          </DialogDescription>
        </DialogHeader>

        {/* Search to add users */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar usuario por nombre o email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Search results */}
          {(searchResults.length > 0 || isSearching) && (
            <div className="rounded-md border bg-muted/30 max-h-40 overflow-y-auto">
              {isSearching ? (
                <div className="p-3 text-center text-sm text-muted-foreground">Buscando...</div>
              ) : (
                searchResults.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-2 hover:bg-accent"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleAssign(user.id)}
                      disabled={isAssigning}
                      className="ml-2 h-7 text-teal-600 hover:text-teal-700 hover:bg-teal-500/10"
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Current users */}
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">
            Usuarios asignados ({areaUsers?.length || 0})
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : areaUsers && areaUsers.length > 0 ? (
            <div className="rounded-md border max-h-[300px] overflow-y-auto">
              {areaUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-2 border-b last:border-b-0 hover:bg-accent"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {user.first_name} {user.last_name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemove(user.id)}
                    className="ml-2 h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-sm text-muted-foreground rounded-md border">
              Sin usuarios asignados
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
